import { UploadOutlined } from "@ant-design/icons"
import { Company } from "@finway-group/shared/lib/models"
import { Button, Checkbox, Col, Divider, Input, Row, Select, Switch, Upload } from "antd"
import Form, { FormInstance } from "antd/lib/form"
import { Store } from "antd/lib/form/interface"
import { RcCustomRequestOptions, RcFile } from "antd/lib/upload/interface"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { API_URL, archiveAfterXDaysDefaults, FINWAY_ADMIN_EMAIL, isDemo } from "Shared/config/consts"
import { FileService } from "Shared/services"
import { getPopupAnchor } from "Shared/utils"
import { checkFileSize, getIBANPrintFormat } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { SECONDARY } from "App.scss"
import CompanySettingsRules from "./rules/companySettings.rules"
import { Plus as PlusIcon } from "react-feather"
import { AutoLogoutEnum, autoLogoutEnumArray } from "@finway-group/shared/lib/models/company/autoLogout.enum"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"

const { Option } = Select

interface CompanySettingsFormInterface {
    isNew: boolean
    formInstance: FormInstance
    company: Company
    onSubmit: (values: Store) => void
}

const CompanySettingsForm: React.FC<CompanySettingsFormInterface> = ({ formInstance, company, isNew, onSubmit }) => {
    const { t } = useTranslation()
    const rules = CompanySettingsRules()
    const loggedInUser = useLoggedInEmployeeProfile()
    const [deliverySameAsBusinessAddress, setDeliverySameAsBusinessAddress] = useStateIfMounted<boolean>(company.deliverySameAsBusinessAddress)
    const [fileList, setFileList] = useStateIfMounted<any>([])
    const [archiveAfterXDaysOptions, setArchiveAfterXDaysOptions] = useStateIfMounted<number[]>(archiveAfterXDaysDefaults)
    const [customArchiveAfterXDays, setCustomArchiveAfterXDays] = useStateIfMounted("")
    const [isAlreadyPaidEnabled, setIsAlreadyPaidEnabled] = useStateIfMounted(company.expensePaymentFlowInformation?.isAlreadyPaidEnabled)
    const [isAlreadyApprovedAndPaidEnabled, setIsAlreadyApprovedAndPaidEnabled] = useStateIfMounted(company.expensePaymentFlowInformation?.isAlreadyApprovedAndPaidEnabled)

    useEffect(() => {
        if (company) {
            formInstance.setFieldsValue({
                ...company,
                iban: company.iban ? getIBANPrintFormat(company.iban) : ``,
                businessAddressAddressLine: company.businessAddress?.addressLine2,
                businessAddressCity: company.businessAddress?.city,
                businessAddressCountry: company.businessAddress?.country,
                businessAddressZip: company.businessAddress?.zip,
                deliveryAddressAddressLine: company.deliveryAddress?.addressLine2,
                deliveryAddressCity: company.deliveryAddress?.city,
                deliveryAddressCountry: company.deliveryAddress?.country,
                deliveryAddressZip: company.deliveryAddress?.zip,
                deliverySameAsBusinessAddress: company.deliverySameAsBusinessAddress,
                isAlreadyPaidEnabled: company.expensePaymentFlowInformation?.isAlreadyPaidEnabled ?? true,
                isAlreadyApprovedAndPaidEnabled: company.expensePaymentFlowInformation?.isAlreadyApprovedAndPaidEnabled ?? false,
                isReportingOnlyEnabled: company.expensePaymentFlowInformation?.isReportingOnlyEnabled ?? false,
            })

            setIsAlreadyPaidEnabled(company.expensePaymentFlowInformation?.isAlreadyPaidEnabled)
            setIsAlreadyApprovedAndPaidEnabled(company.expensePaymentFlowInformation?.isAlreadyApprovedAndPaidEnabled)
        }

        if (company.logo && company.logo.length) {
            const currentFileList = [
                {
                    uid: "1",
                    name: company.name,
                    status: "done",
                    thumbUrl: company.logo,
                    type: "image/png",
                },
            ]
            setFileList(currentFileList)
        }

        return () => {
            formInstance.resetFields()
        }
    }, [])

    const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const ibanPrintFormat = getIBANPrintFormat(e.target.value)
        formInstance.setFieldsValue({
            iban: ibanPrintFormat,
        })

        return {
            iban: ibanPrintFormat,
        }
    }

    // TODO: Move this to consts as it is used on multiple places
    const doFileUpload = async (options: RcCustomRequestOptions) => {
        const { onSuccess, onError, onProgress, file } = options
        if (!checkFileSize(file)) return

        try {
            const response = await FileService.upload(file, "COMPANY_LOGO", onProgress)
            onSuccess(response, response.url as unknown as RcFile)
        } catch (err) {
            onError(err)
        }
    }

    const onFinish = (values: Store) =>
        onSubmit({
            ...values,
            isAlreadyPaidEnabled,
            isAlreadyApprovedAndPaidEnabled,
            deliverySameAsBusinessAddress,
        })

    const archiveAfterXDaysDropdown = (menu: React.ReactElement<any, string | React.JSXElementConstructor<any>>) => (
        <>
            {menu}
            <Divider className="my-6" />
            <Input.Group className="flex flex-row space-x-4" compact>
                <Input type="number" value={customArchiveAfterXDays} onChange={(e) => setCustomArchiveAfterXDays(e.target.value)} />
                <Button
                    onClick={() => {
                        setArchiveAfterXDaysOptions([...archiveAfterXDaysOptions, Number(customArchiveAfterXDays)])
                        setCustomArchiveAfterXDays("")
                    }}
                >
                    <PlusIcon />
                </Button>
            </Input.Group>
        </>
    )

    return (
        <Form form={formInstance} layout="vertical" onFinish={onFinish} autoComplete="off">
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item label={t("input:company_settings.logo")} name="logo">
                        <Upload
                            action={`${API_URL}/files`}
                            headers={{ Authorization: `Bearer ${localStorage.accessToken}` }}
                            listType="picture"
                            fileList={fileList}
                            onRemove={() => setFileList([])}
                            onPreview={() => {}}
                            customRequest={doFileUpload}
                            onChange={(data) => setFileList(data.fileList)}
                            progress={{
                                strokeWidth: 3,
                                showInfo: false,
                                strokeColor: SECONDARY,
                                className: "pr-20",
                            }}
                        >
                            {fileList.length === 0 && <Button icon={<UploadOutlined />}>Upload</Button>}
                        </Upload>
                    </Form.Item>

                    <Form.Item label={t("input:company_settings.name")} name="name" rules={rules.name}>
                        <Input placeholder={t("placeholder:company_settings.name")} required></Input>
                    </Form.Item>

                    <Form.Item label={t("input:company_settings.companyIdentifier")} name="companyIdentifier">
                        <Input placeholder={t("placeholder:company_settings.companyIdentifier")} maxLength={30}></Input>
                    </Form.Item>

                    <Form.Item label={t("input:company_settings.iban")} name="iban" rules={rules.iban}>
                        <Input placeholder={t("placeholder:company_settings.iban")} onChange={handleIBANChange} required></Input>
                    </Form.Item>

                    <Form.Item label={t("input:company_settings.bic")} name="bic" rules={rules.bic}>
                        <Input placeholder={t("placeholder:company_settings.bic")} required></Input>
                    </Form.Item>

                    <Form.Item label={t("input:company_settings.default_creditor_number")} name="defaultCreditorNumber" rules={rules.defaultCreditorNumber}>
                        <Input placeholder={t("placeholder:company_settings.default_creditor_number")}></Input>
                    </Form.Item>

                    <Form.Item label={t("input:company_settings.default_employee_creditor_number")} name="defaultEmployeeCreditorNumber" rules={rules.defaultCreditorNumber}>
                        <Input placeholder={t("placeholder:company_settings.default_employee_creditor_number")}></Input>
                    </Form.Item>

                    <h2 className="mt-20">{t("label:business_address")}</h2>

                    <Form.Item className="mt-20" label={t("input:company_settings.address_line")} name="businessAddressAddressLine">
                        <Input placeholder={t("placeholder:company_settings.address_line")}></Input>
                    </Form.Item>
                    <Form.Item label={t("input:company_settings.city")} name="businessAddressCity" rules={rules.city}>
                        <Input placeholder={t("placeholder:company_settings.city")}></Input>
                    </Form.Item>
                    <Form.Item label={t("input:company_settings.country")} name="businessAddressCountry" rules={rules.countryCode}>
                        <Input placeholder={t("placeholder:company_settings.country")}></Input>
                    </Form.Item>
                    <Form.Item label={t("input:company_settings.zip")} name="businessAddressZip" rules={rules.zip}>
                        <Input placeholder={t("placeholder:company_settings.zip")}></Input>
                    </Form.Item>

                    <h2 className="mt-20">{t("label:delivery_address")}</h2>

                    <Checkbox className="mt-20" checked={deliverySameAsBusinessAddress} onChange={(e) => setDeliverySameAsBusinessAddress(e.target.checked)}>
                        {t("input:company_settings.sameAsBusinessAddress")}
                    </Checkbox>

                    {!deliverySameAsBusinessAddress && (
                        <>
                            <Form.Item className="mt-40" label={t("input:company_settings.address_line")} name="deliveryAddressAddressLine">
                                <Input placeholder={t("placeholder:company_settings.address_line")}></Input>
                            </Form.Item>
                            <Form.Item label={t("input:company_settings.city")} name="deliveryAddressCity" rules={rules.city}>
                                <Input placeholder={t("placeholder:company_settings.city")}></Input>
                            </Form.Item>
                            <Form.Item label={t("input:company_settings.country")} name="deliveryAddressCountry" rules={rules.countryCode}>
                                <Input placeholder={t("placeholder:company_settings.country")}></Input>
                            </Form.Item>
                            <Form.Item label={t("input:company_settings.zip")} name="deliveryAddressZip" rules={rules.zip}>
                                <Input placeholder={t("placeholder:company_settings.zip")}></Input>
                            </Form.Item>
                        </>
                    )}
                </Col>
            </Row>
            <h2 className="mt-20 mb-20">{t("label:features")}</h2>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.card_feature")} name="cardsEnabled" valuePropName="checked" extra={t("info:ask_support_to_enable_card_program")}>
                        <Switch disabled={isDemo || loggedInUser.email !== FINWAY_ADMIN_EMAIL} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.liquidity_management_feature")} name="liquidityManagementEnabled" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.allow_cost_center_2_creation")} name="allowCostCenter2Creation" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.auto_match_feature")} name="autoMatchEnabled" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.allow_banking_payment_auto_matching")} name="bankingPaymentAutoMatchEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.invoice_auto_match_feature")} name="autoInvoiceMatchEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.payment_flow_information.already_paid")} name="isAlreadyPaidEnabled" initialValue={true}>
                        <Switch
                            checked={isAlreadyPaidEnabled}
                            onChange={(value) => {
                                setIsAlreadyPaidEnabled(value)
                                // if the above changed to true, then we gotta disable the other one
                                value && setIsAlreadyApprovedAndPaidEnabled(false)
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.payment_flow_information.already_approved_and_paid")} name="isAlreadyApprovedAndPaidEnabled" initialValue={false}>
                        <Switch
                            checked={isAlreadyApprovedAndPaidEnabled}
                            onChange={(value) => {
                                setIsAlreadyApprovedAndPaidEnabled(value)
                                // if the above changed to true, then we gotta disable the other one
                                value && setIsAlreadyPaidEnabled(false)
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.payment_flow_information.reporting_only")} name="isReportingOnlyEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.tagging_feature")} name="taggingEnabled" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.enable_edit_rights")} name="editingTagsEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.vendor_rules_should_overwrite")} name="vendorRulesOverwriteEmployeeRulesEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.allow_all_to_update_absences")} name="allowAllToUpdateAbsences" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.is_lexware_export_enabled")} name="isLexwareExportEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.allow_workflow_adjustments")} name="allowWorkflowAdjustments" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.show_auto_approver_warning")} name="showAutoApproverWarning" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.show_cost_centers_ids")} name="showCostCentersIds" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.append_audit_trail_to_invoice")} name="isAppendAuditTrailToInvoiceEnabled" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label={t("input:company_settings.vendor_creation_restricted")} name="isVendorCreationRestricted" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xl={12} sm={24}>
                    <Form.Item label={t("label:auto_logout.title")} name="autoLogout" extra={t("info:auto_logout_suggestion")} initialValue={AutoLogoutEnum.FIFTEEN_MINUTES}>
                        <Select getPopupContainer={getPopupAnchor()}>
                            {autoLogoutEnumArray.map((option, index) => (
                                <Option key={index} value={option} label={t(`label:auto_logout.${option.toLowerCase()}`)}>
                                    {t(`label:auto_logout.${option.toLowerCase()}`)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col lg={12} sm={24} className="min-w-100">
                    <Form.Item
                        label={t("input:settings.archiveExpensesAfterXDays")}
                        name="archiveAfterXDays"
                        rules={rules.archiveAfterXDays}
                        extra={t("info:archive.expenseArchiveAfterXDays_settings")}
                        initialValue={90}
                    >
                        <Select className="w-100" getPopupContainer={getPopupAnchor()} dropdownRender={archiveAfterXDaysDropdown}>
                            {archiveAfterXDaysOptions.map((item: number) => (
                                <Option value={item} key={item}>
                                    {item}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={12} className="min-w-100">
                    <Form.Item
                        label={t("input:settings.archiveTransactionsAfterXDays")}
                        name="transactionArchiveAfterXDays"
                        rules={rules.archiveAfterXDays}
                        extra={t("info:archive.transactionArchiveAfterXDays_settings")}
                        initialValue={90}
                    >
                        <Select className="w-100" getPopupContainer={getPopupAnchor()} dropdownRender={archiveAfterXDaysDropdown}>
                            {archiveAfterXDaysOptions.map((item: number) => (
                                <Option value={item} key={item}>
                                    {item}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    )
}

export default CompanySettingsForm
